import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency } from "@/lib/formatters";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

const COLORS = ["var(--analytics-chart-primary, #7b1f40)", "var(--analytics-chart-secondary, #b96382)", "var(--analytics-chart-soft, #d8a4b8)", "var(--analytics-chart-pale, #edd4de)"];

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function DeviceBreakdownChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-breakdown-device", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("breakdown/device", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const total = (data || []).reduce((s: number, d: { spend: number }) => s + d.spend, 0);
  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Device Breakdown" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-device">No device data available</div>
      ) : (
        <div className="px-4 pb-3 h-full flex items-center gap-4" data-testid="chart-device">
          <div className="w-28 h-28 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data || []} innerRadius={30} outerRadius={50} dataKey="spend" nameKey="device" strokeWidth={0}>
                  {(data || []).map((_: unknown, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #DCE3ED" }} formatter={(v: number) => [formatCurrency(v), "Spend"]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {(data || []).map((d: { device: string; spend: number }, i: number) => (
              <div key={d.device} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-gray-600 capitalize">{d.device}</span>
                </div>
                <div className="text-right">
                  <span className="font-medium text-gray-900">{formatCurrency(d.spend)}</span>
                  <span className="text-gray-400 ml-1">({total > 0 ? Math.round((d.spend / total) * 100) : 0}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
